import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SEC, severityColor, statusColor } from './SecurityTheme';
import AlertFeed from './AlertFeed';
import {
  getSecurityFindings,
  getClientSecurityAlerts,
  smartSync,
  SmartSyncResponse,
  testDeltaEngine,
  SecurityFindingsResponse,
  SecurityAlertsResponse,
} from '../../services/api';
import { format, parseISO } from 'date-fns';
import {
  Shield,
  AlertTriangle,
  Activity,
  Radio,
  Play,
  Loader2,
  RefreshCw,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';

interface ClientSecurityDashboardProps {
  clientId: string;
  clientName: string;
  onCollectionComplete?: () => void;
}

export default function ClientSecurityDashboard({ clientId, clientName, onCollectionComplete }: ClientSecurityDashboardProps) {
  const [findings, setFindings] = useState<SecurityFindingsResponse | null>(null);
  const [alerts, setAlerts] = useState<SecurityAlertsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [collectMessage, setCollectMessage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [deltaRunning, setDeltaRunning] = useState(false);
  const [deltaResult, setDeltaResult] = useState<any>(null);

  const fetchData = useCallback(async () => {
    try {
      const [findingsResp, alertsResp] = await Promise.allSettled([
        getSecurityFindings(clientId, { limit: 100 }),
        getClientSecurityAlerts(clientId, { limit: 50 }),
      ]);
      if (findingsResp.status === 'fulfilled' && findingsResp.value.success) {
        setFindings(findingsResp.value);
      }
      if (alertsResp.status === 'fulfilled' && alertsResp.value.success) {
        setAlerts(alertsResp.value);
      }
    } catch (err) {
      console.error('Failed to load client security data:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshAfterCollection = useCallback(async () => {
    setRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      await fetchData();
      onCollectionComplete?.();
    } finally {
      setRefreshing(false);
    }
  }, [fetchData, onCollectionComplete]);

  const formatSyncResult = (result: SmartSyncResponse) => {
    if (!result.success) return result.error || 'Sync failed';
    const parts = [`Sync complete (${result.mode}): ${result.userCount} users, ${result.groupCount} groups, ${result.mailboxCount || 0} mailboxes, ${result.forensicUsers} forensic users`];
    if (result.analysis.findingCount > 0) {
      parts.push(`${result.analysis.findingCount} findings`);
    }
    if (result.analysis.deltaFindingCount > 0) {
      parts.push(`${result.analysis.deltaFindingCount} delta changes`);
    }
    if (result.analysis.alertCreated) {
      parts.push('alert created');
    }
    if (result.exoJobId) {
      parts.push('EXO runbook started');
    }
    return parts.join(', ');
  };

  const handleCollect = async () => {
    setCollecting(true);
    setCollectMessage(null);
    try {
      const result = await smartSync(clientId);
      setCollectMessage(formatSyncResult(result));
      if (result.success) {
        refreshAfterCollection();
      }
    } catch (err: any) {
      setCollectMessage(err.message || 'Sync failed');
    } finally {
      setCollecting(false);
    }
  };

  const handleTestDelta = async () => {
    setDeltaRunning(true);
    setDeltaResult(null);
    try {
      const result = await testDeltaEngine(clientId, false);
      setDeltaResult(result);
      // Refresh data after delta test completes
      refreshAfterCollection();
    } catch (err: any) {
      setDeltaResult({ error: err.message || 'Delta test failed' });
    } finally {
      setDeltaRunning(false);
    }
  };

  // Compute IOC summary
  const iocSummary: Record<string, number> = {};
  if (findings?.byType) {
    for (const [type, count] of Object.entries(findings.byType)) {
      iocSummary[type] = count;
    }
  }
  const iocEntries = Object.entries(iocSummary).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const maxIocCount = iocEntries.length > 0 ? iocEntries[0][1] : 1;

  const criticalCount = findings?.bySeverity?.critical || 0;
  const warningCount = findings?.bySeverity?.warning || 0;
  const infoCount = findings?.bySeverity?.info || 0;
  const openAlerts = alerts ? (alerts.byStatus?.new || 0) + (alerts.byStatus?.acknowledged || 0) + (alerts.byStatus?.investigating || 0) : 0;

  if (loading) {
    return (
      <div className="rounded-lg p-8 flex items-center justify-center" style={{ background: SEC.bg, minHeight: '300px' }}>
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-8 w-8 border-2 mx-auto mb-3"
            style={{ borderColor: SEC.border, borderTopColor: SEC.cyan }}
          />
          <p className="text-sm font-mono" style={{ color: SEC.t3 }}>Loading security data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden" style={{ background: SEC.bg }}>
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-3"
        style={{ background: SEC.header, borderBottom: `1px solid ${SEC.border}` }}
      >
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4" style={{ color: SEC.cyan }} />
          <h3 className="text-xs font-bold tracking-[0.15em] uppercase" style={{ color: SEC.t1 }}>
            Security Operations — {clientName}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleTestDelta()}
            disabled={deltaRunning}
            className="flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded transition-colors"
            style={{
              color: deltaRunning ? SEC.t3 : SEC.amber,
              border: `1px solid ${deltaRunning ? SEC.border : 'rgba(245,158,11,0.4)'}`,
              background: deltaRunning ? 'transparent' : 'rgba(245,158,11,0.1)',
            }}
          >
            {deltaRunning ? (
              <><Loader2 className="h-3 w-3 animate-spin" /> Running...</>
            ) : (
              <><Activity className="h-3 w-3" /> Test Delta</>
            )}
          </button>
          <button
            onClick={fetchData}
            className="p-1 rounded transition-colors hover:bg-white/5"
          >
            <RefreshCw className="h-3.5 w-3.5" style={{ color: SEC.t3 }} />
          </button>
          <Link
            to="/security"
            className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded"
            style={{ color: SEC.t2, border: `1px solid ${SEC.border}` }}
          >
            Full SOC
            <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Collection message */}
      {(collectMessage || refreshing) && (
        <div
          className="px-5 py-2 text-xs flex items-center gap-2"
          style={{
            color: collectMessage?.includes('failed') ? SEC.red : SEC.green,
            background: collectMessage?.includes('failed') ? `${SEC.red}10` : `${SEC.green}10`,
            borderBottom: `1px solid ${SEC.border}`,
          }}
        >
          {collectMessage}
          {refreshing && (
            <span className="flex items-center gap-1 ml-2" style={{ color: SEC.cyan }}>
              <Loader2 className="h-3 w-3 animate-spin" />
              Refreshing...
            </span>
          )}
        </div>
      )}

      {/* Delta Results Panel */}
      {deltaResult && (
        <div
          className="px-5 py-3"
          style={{ background: SEC.panel, borderBottom: `1px solid ${SEC.border}` }}
        >
          {deltaResult.error ? (
            <div className="flex items-center gap-3">
              <div className="text-xs" style={{ color: SEC.amber }}>{deltaResult.error}</div>
              {deltaResult.error.includes('seed') || deltaResult.error.includes('No forensic') ? (
                <button
                  onClick={() => { setDeltaResult(null); handleCollect(); }}
                  disabled={collecting}
                  className="flex items-center gap-1 text-[10px] font-medium px-2.5 py-1 rounded whitespace-nowrap"
                  style={{ color: SEC.cyan, border: `1px solid ${SEC.cyanDim}`, background: SEC.cyanGlow }}
                >
                  {collecting ? <><Loader2 className="h-3 w-3 animate-spin" /> Syncing...</> : <><Play className="h-3 w-3" /> Sync Now</>}
                </button>
              ) : null}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Activity className="h-3.5 w-3.5" style={{ color: SEC.amber }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: SEC.t1 }}>
                    Delta Detection Results
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: SEC.panel2, color: SEC.t3 }}>
                    {deltaResult.currentCollection?.id} vs {deltaResult.previousCollection?.id || 'baseline'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDeltaResult(null)}
                    className="text-[10px] px-1.5 py-0.5 rounded"
                    style={{ color: SEC.t3, border: `1px solid ${SEC.border}` }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              {deltaResult.deltaFindings?.total === 0 ? (
                <p className="text-xs" style={{ color: SEC.t3 }}>No changes detected between scans.</p>
              ) : (
                <>
                  <div className="flex gap-3 mb-2">
                    {deltaResult.deltaFindings?.bySeverity?.critical > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: `${SEC.red}15`, color: SEC.red }}>
                        {deltaResult.deltaFindings.bySeverity.critical} CRITICAL
                      </span>
                    )}
                    {deltaResult.deltaFindings?.bySeverity?.warning > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: `${SEC.amber}15`, color: SEC.amber }}>
                        {deltaResult.deltaFindings.bySeverity.warning} WARNING
                      </span>
                    )}
                    {deltaResult.deltaFindings?.bySeverity?.info > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: `${SEC.cyan}15`, color: SEC.cyan }}>
                        {deltaResult.deltaFindings.bySeverity.info} INFO
                      </span>
                    )}
                  </div>
                  <div className="space-y-1" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {Object.entries(deltaResult.deltaFindings?.byType || {}).map(([type, items]: [string, any]) => (
                      <div key={type}>
                        <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: SEC.cyan }}>
                          {type.replace(/_/g, ' ')}
                        </div>
                        {items.map((f: any, i: number) => (
                          <div key={i} className="flex items-start gap-2 py-0.5 pl-2" style={{ borderLeft: `2px solid ${f.severity === 'critical' ? SEC.red : f.severity === 'warning' ? SEC.amber : SEC.cyan}` }}>
                            <span className="text-[10px] font-mono" style={{ color: SEC.t3, minWidth: 60 }}>{f.user?.split('@')[0] || '?'}</span>
                            <span className="text-[10px]" style={{ color: SEC.t2 }}>{f.summary}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 text-[10px]" style={{ color: SEC.t3 }}>
                    Delta findings are automatically saved during scheduled and manual collections.
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="p-5 space-y-5">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label="FINDINGS"
            value={findings?.total || 0}
            color={criticalCount > 0 ? SEC.red : warningCount > 0 ? SEC.amber : SEC.green}
            sub={`${criticalCount} critical`}
          />
          <SummaryCard
            label="OPEN ALERTS"
            value={openAlerts}
            color={openAlerts > 0 ? SEC.red : SEC.green}
            sub={`${alerts?.total || 0} total`}
          />
          <SummaryCard
            label="IOC TYPES"
            value={iocEntries.length}
            color={SEC.cyan}
            sub={`${findings?.total || 0} detections`}
          />
          <SummaryCard
            label="SEVERITY"
            value={criticalCount > 0 ? 'CRITICAL' : warningCount > 0 ? 'WARNING' : 'CLEAR'}
            color={criticalCount > 0 ? SEC.red : warningCount > 0 ? SEC.amber : SEC.green}
            sub={`${criticalCount}C / ${warningCount}W / ${infoCount}I`}
            isText
          />
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* IOC Summary */}
          <div
            className="rounded-lg overflow-hidden"
            style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
          >
            <div
              className="flex items-center gap-2 px-4 py-2.5"
              style={{ borderBottom: `1px solid ${SEC.border}` }}
            >
              <Radio className="h-3.5 w-3.5" style={{ color: SEC.amber }} />
              <h4 className="text-[10px] font-semibold tracking-[0.15em] uppercase" style={{ color: SEC.t1 }}>
                IOC Summary
              </h4>
            </div>
            <div className="px-4 py-3 space-y-2">
              {iocEntries.length === 0 ? (
                <p className="text-center py-4 text-xs" style={{ color: SEC.t3 }}>
                  No IOC detections for this client
                </p>
              ) : (
                iocEntries.map(([type, count]) => {
                  const pct = (count / maxIocCount) * 100;
                  return (
                    <div key={type}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] font-medium truncate" style={{ color: SEC.t2 }}>
                          {type.replace(/_/g, ' ')}
                        </span>
                        <span className="text-[10px] font-mono ml-2" style={{ color: SEC.t1 }}>
                          {count}
                        </span>
                      </div>
                      <div className="w-full h-1 rounded-full overflow-hidden" style={{ background: SEC.border }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${pct}%`,
                            background: count === maxIocCount ? SEC.red : SEC.cyan,
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Alert Feed */}
          <div
            className="rounded-lg overflow-hidden"
            style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
          >
            <div
              className="flex items-center justify-between px-4 py-2.5"
              style={{ borderBottom: `1px solid ${SEC.border}` }}
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-3.5 w-3.5" style={{ color: SEC.red }} />
                <h4 className="text-[10px] font-semibold tracking-[0.15em] uppercase" style={{ color: SEC.t1 }}>
                  Recent Alerts
                </h4>
                {openAlerts > 0 && (
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ color: SEC.red, background: `${SEC.red}20` }}
                  >
                    {openAlerts}
                  </span>
                )}
              </div>
              <Link
                to="/security/alerts"
                className="text-[10px] flex items-center gap-0.5"
                style={{ color: SEC.cyan }}
              >
                View All
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <AlertFeed compact maxItems={10} autoRefreshMs={60000} clientId={clientId} />
          </div>
        </div>

        {/* Recent Findings Table */}
        {findings && findings.findings.length > 0 && (
          <div
            className="rounded-lg overflow-hidden"
            style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
          >
            <div
              className="flex items-center gap-2 px-4 py-2.5"
              style={{ borderBottom: `1px solid ${SEC.border}` }}
            >
              <Activity className="h-3.5 w-3.5" style={{ color: SEC.cyan }} />
              <h4 className="text-[10px] font-semibold tracking-[0.15em] uppercase" style={{ color: SEC.t1 }}>
                Recent Findings
              </h4>
              <span className="text-[10px] font-mono" style={{ color: SEC.t3 }}>
                {findings.total} total
              </span>
            </div>
            <div className="overflow-x-auto" style={{ maxHeight: '300px' }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: SEC.panel2, borderBottom: `1px solid ${SEC.border}` }}>
                    <th className="text-left px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Severity</th>
                    <th className="text-left px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Type</th>
                    <th className="text-left px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Summary</th>
                    <th className="text-left px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Status</th>
                    <th className="text-right px-4 py-2 font-medium uppercase tracking-wider" style={{ color: SEC.t3, fontSize: '10px' }}>Detected</th>
                  </tr>
                </thead>
                <tbody>
                  {findings.findings.slice(0, 20).map((f) => {
                    const sevClr = severityColor(f.severity);
                    const statClr = statusColor(f.status);
                    return (
                      <tr
                        key={f.id}
                        className="transition-colors hover:bg-white/[0.02]"
                        style={{ borderBottom: `1px solid ${SEC.border}` }}
                      >
                        <td className="px-4 py-2">
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                            style={{ background: `${sevClr}20`, color: sevClr }}
                          >
                            {f.severity}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded"
                            style={{ background: SEC.cyanGlow, color: SEC.cyanDim }}
                          >
                            {f.type.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-2 truncate max-w-[250px]" style={{ color: SEC.t1 }}>
                          {f.summary}
                        </td>
                        <td className="px-4 py-2">
                          <span className="flex items-center gap-1 text-[10px]" style={{ color: statClr }}>
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ background: statClr }}
                            />
                            {f.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right font-mono" style={{ color: SEC.t3 }}>
                          {formatDate(f.detectedAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  color,
  sub,
  isText = false,
}: {
  label: string;
  value: string | number;
  color: string;
  sub: string;
  isText?: boolean;
}) {
  return (
    <div
      className="rounded-lg p-3 relative overflow-hidden"
      style={{ background: SEC.panel, border: `1px solid ${SEC.border}` }}
    >
      <div
        className="absolute top-0 left-0 w-full h-0.5"
        style={{ background: `linear-gradient(90deg, ${color}60, transparent)` }}
      />
      <p className="text-[9px] font-bold tracking-[0.15em] uppercase mb-0.5" style={{ color: SEC.t3 }}>
        {label}
      </p>
      <p
        className={`font-bold font-mono ${isText ? 'text-sm' : 'text-xl'}`}
        style={{ color }}
      >
        {value}
      </p>
      <p className="text-[9px] mt-0.5" style={{ color: SEC.t4 }}>{sub}</p>
    </div>
  );
}

function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'MMM d HH:mm');
  } catch {
    return dateStr;
  }
}
